import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { Confirm, ModalConstants } from "components/OnlineModal";
import dayjs from "dayjs";
import instance from "utils/axios";
import CellRender from "./CellRender";
import RowDateRender from "./RowDateRender";
import languageService from "features/language/languageService";

import type { AttendanceDto, AttendanceState, AttendanceUser, AttendanceUserWData, ClassMeetupResponse, StudentClassMeetupDto, StudentClassMeetupDtoWithDate } from "./Attendance_types";

/* All the redux stuff needed for attendance feature. LäraNära Portal */

const initialState: AttendanceState = {
    students: [],
    meetups: [],
    columnDefs: [],
    rowData: []
}

/****************** reducers ************************/
const attendanceSlice = createSlice({
    name: "attendance",
    initialState: initialState,
    reducers: {

        setMeetups(state, action: { payload: { Students: AttendanceUser[], Meetups: StudentClassMeetupDto[] } }) {
            state.students = action.payload.Students;
            state.meetups = action.payload.Meetups;
            state.columnDefs = makeColumnDefs(action.payload.Students);
            const rowData = [];
            action.payload.Meetups.forEach(d => rowData.push(makeRowData(d, action.payload.Students)));

            state.rowData = rowData;

        },


        addMeetup(state, action: PayloadAction<StudentClassMeetupDto>) {
            state.meetups.push(action.payload);
            const rowData = [];
            state.meetups.forEach(d => rowData.push(makeRowData(d, state.students)));
            state.rowData = rowData;
        },


        removeMeetup(state: AttendanceState, action: PayloadAction<number>) {
            state.meetups = state.meetups.filter(m => m.Id !== action.payload)

            state.rowData = state.rowData.filter(data => (data.date as StudentClassMeetupDtoWithDate).Id !== action.payload);
        },


        setRowdata(state: AttendanceState, action: PayloadAction<{ [key: string]: object }[]>) {
            state.rowData = action.payload;
        },

        setAttendance(state: AttendanceState, action:PayloadAction<AttendanceDto>) {
            state.meetups.forEach(m => {
                const att = m.Attendances.find(a => a.Id === action.payload.Id);
                if (att) {
                    att.Present = action.payload.Present;
                }
            });

            state.rowData.forEach(row => {
                const userData = row[action.payload.UserId] as AttendanceUserWData;
                if (userData && userData.state && userData.state.Id === action.payload.Id) {
                    userData.state = action.payload
                }
            })

        },

        insertNewAttendance(state: AttendanceState,
            action: PayloadAction<{ attendance: AttendanceDto, meetupId: number }>) {

            const meetup = state.meetups.find(m => m.Id === action.payload.meetupId);
            meetup.Attendances.push(action.payload.attendance);
            meetup.AttendancesByUserId[action.payload.attendance.UserId] = action.payload.attendance;

            const row = state.rowData.find(row => (row.date as StudentClassMeetupDtoWithDate).Id === action.payload.meetupId);
            if (row) {
                const userData = row[action.payload.attendance.UserId] as AttendanceUserWData;
                if (userData) {
                    userData.state = action.payload.attendance;
                }
            }

        }

    }

});

/****************** service methods ************************/
const services = {
    getMeetup: (classId: string) => async (dispatch) => {

        dispatch(attendanceDuck.setMeetups({
            Students: [],
            Meetups: []
        }));
        const resp = await instance.get<ClassMeetupResponse>(urls.GET_MEETUP_URL(classId))


        const data = resp.data;

        // create lookup table
        data.Meetups.forEach(m => {
            m.AttendancesByUserId = {};
            m.Attendances.forEach(a => m.AttendancesByUserId[a.UserId] = a);
        })

        dispatch(attendanceDuck.setMeetups(data));
    },

    createMeetup: (classId: string, header: string, date: Date) => async (dispatch) => {

        const data = {
            Dato: date,
            Header: header,
            ClassId: classId
        }
        /**
         * @type{ import("axios").AxiosResponse<import("./Attendance_types").StudentClassMeetupDto> }
         */
        const resp = await instance.post(urls.CREATE_MEETUP_URL(classId), data)

        const meetup = resp.data;

        // create lookup table
        meetup.AttendancesByUserId = {};
        meetup.Attendances.forEach(a => meetup.AttendancesByUserId[a.UserId] = a);


        dispatch(attendanceDuck.addMeetup(meetup));
    },

    deleteMeetup: (classId, meetupId) => async (dispatch) => {

        const t = languageService;

        const result = await Confirm({
            buttons: [],
            backdrop: 'static',
            class: null,
            title: t.getText("delete"),
            message: (<div dangerouslySetInnerHTML={{ __html: t.getText('delete_general_confirm') }}></div>),
            languageService: t,
            type: "yes/no",
            okOnEnter: true
        });

        if (result === ModalConstants.Yes) {
            await instance.delete(urls.DELETE_MEETUP_URL({ classId, meetupId }))
            dispatch(attendanceDuck.removeMeetup(meetupId));
        }
    },


    alterAttendance: (classId, attendanceId, state) => async (dispatch) => {

        const resp = await instance.patch<AttendanceDto>(urls.UPDATE_ATTENDANCE_URL({ classId, attendanceId, state }), null);
        const attendance = resp.data;
        dispatch(attendanceDuck.setAttendance(attendance));
    },

    addAttendance: (classId, meetupId, userId) => async (dispatch) => {


        const resp = await instance.patch<AttendanceDto>(urls.ADD_ATTENDANCE_URL({ classId, meetupId, userId }), null);
        const attendance = resp.data;
        dispatch(attendanceDuck.insertNewAttendance({ attendance, meetupId }));
    },

};

const makeRowData = (m: StudentClassMeetupDto, students: AttendanceUser[]) => {
    const data: Record<string, object | StudentClassMeetupDtoWithDate> = {};

    data.date = { ...m, Dato: dayjs(m.Dato).format("YYYY-MM-DD") };

    students.filter(u => !u.IsArchived).forEach(s => {
        data[s.Id] = { ...s, state: m.AttendancesByUserId[s.Id] };
    });

    return data;
}

const makeColumnDefs = (students) => {

    return [
        { field: "date", pinned: 'left', width: 200, lockPosition: true, cellRenderer: RowDateRender },
        ...students.filter(u => !u.IsArchived).map(s => {
            return {
                field: s.Id,
                headerComponentParams: { stud: s },
                cellRenderer: CellRender,
                width: 66,
                lockPosition: true,
                tooltipField: s.Id,
            }
        })];

}

const urls = {
    GET_MEETUP_URL: (classId) => `attendance/${classId}`,
    CREATE_MEETUP_URL: (classId) => `attendance/${classId}`,
    UPDATE_ATTENDANCE_URL: ({ classId, attendanceId, state }) => `attendance/${classId}/updateattendance/${attendanceId}/${state}`,
    ADD_ATTENDANCE_URL: ({ classId, meetupId, userId }) => `attendance/${classId}/addattendance/${userId}/${meetupId}`,
    DELETE_MEETUP_URL: ({ classId, meetupId }) => `attendance/${classId}/delete/${meetupId}`
}

export const attendanceDuck = { ...attendanceSlice.actions }
export const attendanceService = { ...services }
export default attendanceSlice.reducer;


